import React from 'react';
import { ReactComponent as ResolveLogoSvg } from 'icons/Resolve.svg';
import classNames from 'classnames';
import { footerLinks, FooterLinks } from './utils';
import { useTranslation } from 'react-i18next';
import { Button, Collapsible } from 'ncoded-component-library';
import useMatchMedia from 'hooks/useMatchMedia';
import ArrowupIcon from 'icons/Arrowup.icon';
import { ExtractPropTypes } from 'types';
import { useRenderLinks } from './hooks/useRenderLinks';
import { Link } from 'react-router-dom';
import { NEW_WINDOW_PROPS } from 'data/global';

import './Footer.styles.scss';
import './Footer.styles.responsive.scss';

type FooterProps = {
  className?: string;
  reffering: boolean;
  onRefer: () => void;
};

const year = new Date().getFullYear();

export const CollapsibleFooterSection: React.FC<
  ExtractPropTypes<typeof Collapsible> & {
    title: string;
    externLink?: boolean;
    links: Array<FooterLinks>;
  }
> = ({ links, title, externLink, ...rest }) => {
  const renderLinks = useRenderLinks();
  const { t } = useTranslation();

  if (!links?.length) return null;

  if (links?.length === 1)
    return externLink ? (
      <a
        href={links[0].href}
        aria-label={t(links[0].label)}
        className="footer-single-link"
        {...NEW_WINDOW_PROPS}
      >
        {t(links[0].label)}
      </a>
    ) : (
      <Link className="footer-single-link" to={links[0].href}>
        {t(links[0].label)}
      </Link>
    );

  return (
    <Collapsible
      {...rest}
      className="footer__section--collapsible"
      trigger={
        <>
          <p className="section-title">{title}</p>
          <ArrowupIcon className="section-arrow-icon" />
        </>
      }
    >
      <ul className="collapsible-container">
        {renderLinks(links, externLink)}
      </ul>
    </Collapsible>
  );
};

const Footer: React.FC<FooterProps> = (props) => {
  const { className, reffering, onRefer } = props;

  const classes = classNames('footer', className);

  const { t } = useTranslation();
  const renderLinks = useRenderLinks();
  const isPhablet = useMatchMedia('isPhablet');

  return (
    <footer className={classes}>
      <div className="footer__container">
        {reffering && (
          <Button
            className="refer-friend-btn"
            onClick={onRefer}
            variant="outline"
          >
            {t('referAFriend')}
          </Button>
        )}

        <div className="footer__content">
          <a
            href="/"
            aria-label="Resolve logo"
            className="footer__resolve-logo"
          >
            <ResolveLogoSvg />
          </a>

          <div className="footer__sections">
            {Object.entries(footerLinks).map(([key, value]) =>
              isPhablet ? (
                <CollapsibleFooterSection
                  title={t(value.title)}
                  links={value.links}
                  key={key}
                  externLink={true}
                />
              ) : (
                <div className="footer__section" key={key}>
                  <p className="footer__section__title">{t(value.title)}</p>
                  {value.links.length && (
                    <ul className="footer__section__links">
                      {renderLinks(value.links, true)}
                    </ul>
                  )}
                </div>
              ),
            )}
          </div>
        </div>

        <div className="footer__separator" />
        <p className="footer__copyright">© Resolve {year}</p>
      </div>
    </footer>
  );
};

export default React.memo(Footer);
